/**
 * Notification Center Store — the durable, app-wide log of issues the user may
 * have missed. This is the persistent counterpart to notificationStore.ts
 * (which holds only transient toasts): entries here survive dismissal of the
 * toast, survive a reload, and are grouped/filtered in the header bell popover.
 *
 * MERIDIAN §Notifications → "Notification Center (persistent)": the durable log
 * of events the user may have missed, opened from a bell in the header, grouped
 * reverse-chronologically, filterable by source, with read/unread state.
 *
 * Sources are open-ended (network anomalies, favorite-station liveness, sync,
 * ingestion, …) — anything app-wide worth surfacing pushes an entry here via
 * `upsert`. Entries are DEDUPED by a stable `id` so a recurring condition (a
 * station that stays silent across many poll cycles) updates its existing entry
 * instead of flooding the log.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Where an issue originated — drives the filter chips + the source icon/label. */
export type NotificationSource = 'network' | 'favorite' | 'qc' | 'sync' | 'ingestion' | 'system';

export type NotificationSeverity = 'critical' | 'warning' | 'info';

export interface CenterNotification {
  /** Stable dedup key. Re-`upsert`ing the same id updates the entry in place. */
  id: string;
  source: NotificationSource;
  severity: NotificationSeverity;
  title: string;
  message: string;
  /** First time this issue was seen (ms epoch). Preserved across re-upserts. */
  createdAt: number;
  /** Most recent time the underlying condition was reported (ms epoch). */
  updatedAt: number;
  read: boolean;
  /** Optional in-app deep link the row navigates to (e.g. `/network?dataSource=X`). */
  href?: string;
}

/** The caller-facing shape for pushing an issue — timestamps + read are managed. */
export interface NotificationInput {
  id: string;
  source: NotificationSource;
  severity: NotificationSeverity;
  title: string;
  message: string;
  href?: string;
}

// ─── Config ──────────────────────────────────────────────────────────────────

/** Hard cap on stored entries — oldest are trimmed first so the log can't grow
 *  unbounded across long sessions / many poll cycles. */
const MAX_ENTRIES = 200;
const STORAGE_KEY = 'stationExplorer_notificationCenter';

// ─── Store ───────────────────────────────────────────────────────────────────

interface NotificationCenterState {
  notifications: CenterNotification[];
}

interface NotificationCenterActions {
  /**
   * Add an issue, or update the existing one with the same id. On update we
   * refresh severity/title/message/href + bump `updatedAt`, and — importantly —
   * re-mark it unread when the severity ESCALATES, so a warning that becomes
   * critical resurfaces. A steady/decreasing condition stays read if the user
   * already read it.
   */
  upsert: (input: NotificationInput, at?: number) => void;
  /** Bulk variant — reconcile many issues from one source in a single commit. */
  upsertMany: (inputs: NotificationInput[], at?: number) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  remove: (id: string) => void;
  /** Drop every entry from a given source (e.g. when a source reports "all clear"). */
  clearSource: (source: NotificationSource) => void;
  clearAll: () => void;
}

type NotificationCenterStore = NotificationCenterState & NotificationCenterActions;

const SEVERITY_RANK: Record<NotificationSeverity, number> = { info: 0, warning: 1, critical: 2 };

function trim(list: CenterNotification[]): CenterNotification[] {
  if (list.length <= MAX_ENTRIES) return list;
  // Keep the most-recently-updated MAX_ENTRIES.
  return [...list].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, MAX_ENTRIES);
}

function applyUpsert(
  list: CenterNotification[],
  input: NotificationInput,
  at: number,
): CenterNotification[] {
  const idx = list.findIndex((n) => n.id === input.id);
  if (idx === -1) {
    return [
      ...list,
      {
        ...input,
        createdAt: at,
        updatedAt: at,
        read: false,
      },
    ];
  }
  const prev = list[idx];
  const escalated = SEVERITY_RANK[input.severity] > SEVERITY_RANK[prev.severity];
  const next: CenterNotification = {
    ...prev,
    source: input.source,
    severity: input.severity,
    title: input.title,
    message: input.message,
    href: input.href,
    updatedAt: at,
    // Resurface (unread) only on escalation; otherwise preserve read state.
    read: escalated ? false : prev.read,
  };
  const copy = list.slice();
  copy[idx] = next;
  return copy;
}

export const useNotificationCenterStore = create<NotificationCenterStore>()(
  persist(
    (set) => ({
      notifications: [],

      upsert: (input, at = Date.now()) =>
        set((state) => ({ notifications: trim(applyUpsert(state.notifications, input, at)) })),

      upsertMany: (inputs, at = Date.now()) =>
        set((state) => {
          let next = state.notifications;
          for (const input of inputs) next = applyUpsert(next, input, at);
          return { notifications: trim(next) };
        }),

      markRead: (id) =>
        set((state) => ({
          notifications: state.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
        })),

      markAllRead: () =>
        set((state) => ({
          notifications: state.notifications.map((n) => (n.read ? n : { ...n, read: true })),
        })),

      remove: (id) =>
        set((state) => ({ notifications: state.notifications.filter((n) => n.id !== id) })),

      clearSource: (source) =>
        set((state) => ({ notifications: state.notifications.filter((n) => n.source !== source) })),

      clearAll: () => set({ notifications: [] }),
    }),
    {
      name: STORAGE_KEY,
      // Persist only the log itself; actions are re-created on load.
      partialize: (state) => ({ notifications: state.notifications }),
    },
  ),
);

// ─── Selectors ───────────────────────────────────────────────────────────────
// Return-stable selectors: Zustand v5 (useSyncExternalStore) re-renders whenever
// a selector returns a new reference. Derived arrays/counts are memoised on the
// input identity so an unrelated store write doesn't churn subscribers.

/** Unread count, capped for display at "9+" by the badge (raw count returned here). */
export const selectUnreadCount = (state: NotificationCenterState): number =>
  state.notifications.reduce((acc, n) => (n.read ? acc : acc + 1), 0);

let _prevSortedInput: CenterNotification[] = [];
let _prevSorted: CenterNotification[] = [];
/** All entries, newest `updatedAt` first (stable reference until the log changes). */
export const selectSortedNotifications = (state: NotificationCenterState): CenterNotification[] => {
  if (state.notifications === _prevSortedInput) return _prevSorted;
  _prevSortedInput = state.notifications;
  _prevSorted = [...state.notifications].sort((a, b) => b.updatedAt - a.updatedAt);
  return _prevSorted;
};
